import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Download,
  Info,
} from "lucide-react";

const ContactUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  onDownloadTemplate,
  isUploading = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setErrors((prev) => ({ ...prev, file: null }));
      } else {
        setErrors((prev) => ({
          ...prev,
          file: "Please select a valid CSV or Excel file",
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!selectedFile) newErrors.file = "Please select a file";
    if (!groupName.trim()) newErrors.groupName = "Please enter a group name";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Simulate upload progress
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await onUpload(selectedFile, groupName.trim());
      setUploadProgress(100);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setErrors({ submit: error.message || "Upload failed" });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setGroupName("");
    setErrors({});
    setUploadProgress(0);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Upload Contacts
              </h2>
              <p className="mt-1 text-gray-600">
                Add contacts to your account by uploading a CSV or Excel file
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 rounded-lg transition-colors duration-200 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Upload Progress */}
          {isUploading && uploadProgress > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Uploading...
                </span>
                <span className="text-sm text-gray-500">{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <motion.div
                  className="h-2 bg-blue-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Group Name Input */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Name of Group *
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (errors.groupName) {
                    setErrors((prev) => ({ ...prev, groupName: null }));
                  }
                }}
                placeholder="Enter a name for this contact group"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.groupName
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                disabled={isUploading}
              />
              {errors.groupName && (
                <div className="flex gap-2 items-center mt-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.groupName}</span>
                </div>
              )}
            </div>

            {/* File Upload Area */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Contact File *
              </label>

              <div
                className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 ${
                  dragActive
                    ? "border-blue-400 bg-blue-50"
                    : errors.file
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />

                <div className="text-center">
                  {selectedFile ? (
                    <div className="flex gap-3 justify-center items-center">
                      <div className="p-3 bg-red-100 rounded-lg">
                        <FileText className="w-6 h-6 text-red-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">
                          {selectedFile.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-red-500" />
                    </div>
                  ) : (
                    <>
                      <div className="inline-block p-4 mb-4 bg-gray-100 rounded-lg">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="mb-2 text-lg font-medium text-gray-900">
                        Drop your file here, or{" "}
                        <span className="text-blue-600">browse</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Supports XLS and XLSX files up to 10MB
                      </p>
                    </>
                  )}
                </div>
              </div>

              {errors.file && (
                <div className="flex gap-2 items-center mt-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.file}</span>
                </div>
              )}
            </div>

            {/* Template Download */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex gap-3 items-start">
                <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="flex-1">
                  <h4 className="mb-1 font-medium text-blue-900">
                    Need a template?
                  </h4>
                  <p className="mb-3 text-sm text-blue-700">
                    Download our template to ensure your contacts are formatted
                    correctly.
                  </p>
                  <button
                    type="button"
                    onClick={onDownloadTemplate}
                    className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg transition-colors duration-200 hover:bg-blue-200"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="flex gap-2 items-center p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="w-5 h-5" />
                <span>{errors.submit}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-6 py-3 font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors duration-200 hover:bg-gray-50"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isUploading || !selectedFile || !groupName.trim()}
              >
                {isUploading ? "Uploading..." : "Upload Contacts"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ContactUploadModal;
